// src/cockpit4/components/BudgetManager.tsx
// ============================================
// 预算管理组件（财务中心 P1-功能4）
//
// 功能规格：
//   1. 年度/季度/月度预算制定：按类别（工资/房租/营销/采购/差旅/税费/其他）录入预算金额
//   2. 预算 vs 实际对比分析：柱状图对比每个类别的预算金额与实际支出
//   3. 预算执行率和偏差分析：执行率 = 实际/预算，偏差 = 实际 - 预算
//   4. 超支预警：实际 > 预算 时红色标记，显示超支金额和百分比
//   5. 预算调整流程：可调整预算金额，记录调整历史
//   6. 预算版本管理：保存历史版本，可回溯
//   7. 时间范围切换：月度/季度/年度
//   8. 可视化：recharts BarChart 柱状图 + 执行率进度条
//
// 数据来源（禁止假数据）：
//   - 预算数据：localStorage（key: xeaos_finance_budgets）持久化用户人工录入
//   - 实际支出：从 RevenueEngine 和 TransactionList 的支出数据聚合
//     通过 localStorage（key: xeaos_finance_transactions）读取交易流水
//   - 无数据时显示"暂无预算 / 点击创建"
//
// 权限：财务中心仅超级管理员开放
//   const isSuperAdmin = useUserStore((s) => s.isSuperAdmin())
//   非超管显示权限提示
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wallet,
  Plus,
  X,
  RefreshCw,
  Loader2,
  Trash2,
  Pencil,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Save,
  History,
  Layers,
  ChevronLeft,
  ChevronRight,
  Database,
  Target,
  Percent,
  Coins,
  Lock,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  Cell,
} from "recharts";
import { useUserStore } from "../../stores/userStore";
// Phase 5 财务 Vault 化：预算数据写透 Vault（跨设备唯一真相源）+ 挂载回填
import { writeFinanceJson, hydrateFinanceKey } from "../../services/financeVaultStorage";

// ============================================
// 类型定义
// ============================================

/** 预算周期 */
type BudgetPeriod = "monthly" | "quarterly" | "yearly";

/** 预算类别 */
type BudgetCategory =
  | "salary"
  | "rent"
  | "marketing"
  | "procurement"
  | "travel"
  | "tax"
  | "other";

/** 预算项（单类别） */
interface BudgetItem {
  category: BudgetCategory;
  /** 预算金额 */
  budgetedAmount: number;
  /** 实际支出（从交易数据聚合，加载时动态计算） */
  actualAmount: number;
}

/** 调整历史记录 */
interface AdjustmentRecord {
  /** 时间戳 */
  timestamp: string;
  /** 调整类别 */
  category: BudgetCategory;
  /** 调整前金额 */
  oldAmount: number;
  /** 调整后金额 */
  newAmount: number;
  /** 操作人 */
  operator: string;
}

/** 预算历史版本快照 */
interface BudgetVersion {
  /** 版本号 */
  version: number;
  /** 快照时间 */
  snapshotAt: string;
  /** 快照时的预算项 */
  items: BudgetItem[];
  /** 总预算 */
  totalBudget: number;
  /** 操作人 */
  operator: string;
}

/** 预算 */
interface Budget {
  id: string;
  period: BudgetPeriod;
  year: number;
  /** monthly/quarterly 时使用 */
  month?: number;
  /** quarterly 时使用 */
  quarter?: number;
  items: BudgetItem[];
  totalBudget: number;
  totalActual: number;
  createdAt: string;
  updatedAt: string;
  /** 调整历史 */
  adjustmentHistory?: AdjustmentRecord[];
  /** 历史版本快照 */
  versions?: BudgetVersion[];
}

/** 从 localStorage 读取的交易流水（防御性结构，兼容多种形态） */
interface FinanceTransaction {
  id?: string;
  /** 金额（正数） */
  amount?: number;
  /** 收支方向：expense=支出，income=收入 */
  direction?: "income" | "expense";
  /** 类别字符串（用于匹配预算类别） */
  category?: string;
  type?: string;
  /** 交易日期 ISO */
  date?: string;
  /** 金额带符号（负=支出），兼容直接存储 */
  value?: number;
  sign?: number;
}

// ============================================
// 常量配置
// ============================================

const BUDGETS_KEY = "xeaos_finance_budgets";
const TRANSACTIONS_KEY = "xeaos_finance_transactions";

/** 预算类别中文映射 + 颜色 + 图标 */
const CATEGORY_META: Record<
  BudgetCategory,
  { label: string; color: string; keywords: string[] }
> = {
  salary: {
    label: "工资",
    color: "#60A5FA",
    keywords: ["salary", "工资", "薪资", "薪酬", "人工", "工费"],
  },
  rent: {
    label: "房租",
    color: "#A78BFA",
    keywords: ["rent", "房租", "租金", "场地", "物业", "租赁"],
  },
  marketing: {
    label: "营销",
    color: "#F472B6",
    keywords: ["marketing", "营销", "推广", "广告", "市场", "投放", "宣传"],
  },
  procurement: {
    label: "采购",
    color: "#34D399",
    keywords: ["procurement", "采购", "进货", "物料", "原料", "供应链"],
  },
  travel: {
    label: "差旅",
    color: "#FBBF24",
    keywords: ["travel", "差旅", "出差", "报销", "交通", "住宿", "机票"],
  },
  tax: {
    label: "税费",
    color: "#F87171",
    keywords: ["tax", "税", "税费", "增值税", "所得税", "附加税"],
  },
  other: {
    label: "其他",
    color: "#94A3B8",
    keywords: ["other", "其他", "杂项", "办公", "水电", "杂费"],
  },
};

/** 所有类别（顺序固定） */
const ALL_CATEGORIES: BudgetCategory[] = [
  "salary",
  "rent",
  "marketing",
  "procurement",
  "travel",
  "tax",
  "other",
];

/** 周期配置 */
const PERIOD_META: Record<
  BudgetPeriod,
  { label: string; shortLabel: string; color: string }
> = {
  monthly: { label: "月度预算", shortLabel: "月", color: "#3B82F6" },
  quarterly: { label: "季度预算", shortLabel: "季", color: "#10B981" },
  yearly: { label: "年度预算", shortLabel: "年", color: "#A855F7" },
};

// ============================================
// localStorage 持久化
// ============================================

/** 加载所有预算 */
function loadBudgets(): Budget[] {
  try {
    const raw = localStorage.getItem(BUDGETS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as Budget[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

/** 保存所有预算（写透 Vault + localStorage） */
function saveBudgets(budgets: Budget[]): void {
  try {
    localStorage.setItem(BUDGETS_KEY, JSON.stringify(budgets));
  } catch {
    // 静默降级
  }
  // Phase 5：写透 Vault（失败静默降级到 localStorage，不抛异常）
  void writeFinanceJson("budgets.json", BUDGETS_KEY, budgets);
}

/** 加载交易流水（实际支出数据来源） */
function loadTransactions(): FinanceTransaction[] {
  try {
    const raw = localStorage.getItem(TRANSACTIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // 兼容两种存储形态：直接数组 / { transactions: [] }
    const list: unknown = Array.isArray(parsed)
      ? parsed
      : parsed?.transactions;
    if (!Array.isArray(list)) return [];
    return list as FinanceTransaction[];
  } catch {
    return [];
  }
}

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(): string {
  return `budget-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** 获取当前季度（1-4） */
function getCurrentQuarter(): number {
  return Math.ceil((new Date().getMonth() + 1) / 3);
}

/** 格式化金额（元 → 万元/元），防御 undefined/NaN */
function formatMoney(amount: number): string {
  const num = Number(amount);
  if (!Number.isFinite(num) || num < 0) return "¥ 0";
  if (num >= 10000) {
    return `¥ ${(num / 10000).toFixed(2)} 万`;
  }
  return `¥ ${num.toLocaleString()}`;
}

/** 格式化金额（带正负号，用于偏差显示） */
function formatSignedMoney(amount: number): string {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "¥ 0";
  const sign = num > 0 ? "+" : num < 0 ? "-" : "";
  const abs = Math.abs(num);
  if (abs >= 10000) {
    return `${sign}¥ ${(abs / 10000).toFixed(2)} 万`;
  }
  return `${sign}¥ ${abs.toLocaleString()}`;
}

/** 格式化日期时间 */
function formatDateTime(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)}`;
}

/**
 * 将交易流水的 category 字符串映射到预算类别
 * 通过关键词模糊匹配，未命中归为 other
 */
function matchTransactionCategory(
  txCategory: string | undefined
): BudgetCategory {
  if (!txCategory) return "other";
  const lower = String(txCategory).toLowerCase();
  for (const cat of ALL_CATEGORIES) {
    const meta = CATEGORY_META[cat];
    if (meta.keywords.some((kw) => lower.includes(kw.toLowerCase()))) {
      return cat;
    }
  }
  return "other";
}

/**
 * 判断交易是否为支出
 * 兼容三种形态：direction=expense / sign<0 / value<0
 */
function isExpenseTransaction(tx: FinanceTransaction): boolean {
  if (tx.direction) return tx.direction === "expense";
  if (typeof tx.sign === "number") return tx.sign < 0;
  if (typeof tx.value === "number") return tx.value < 0;
  // 默认按支出处理（无方向标识时）
  return true;
}

/** 提取交易金额（正数） */
function extractAmount(tx: FinanceTransaction): number {
  if (typeof tx.amount === "number" && tx.amount > 0) return tx.amount;
  if (typeof tx.value === "number") return Math.abs(tx.value);
  return 0;
}

/**
 * 聚合指定周期内的实际支出（按类别）
 * 从 xeaos_finance_transactions 读取真实交易流水
 * @returns Record<category, amount>
 */
function aggregateActualByCategory(
  transactions: FinanceTransaction[],
  period: BudgetPeriod,
  year: number,
  month?: number,
  quarter?: number
): Record<BudgetCategory, number> {
  const result = {} as Record<BudgetCategory, number>;
  for (const cat of ALL_CATEGORIES) result[cat] = 0;

  for (const tx of transactions) {
    // 仅统计支出
    if (!isExpenseTransaction(tx)) continue;
    const amount = extractAmount(tx);
    if (amount <= 0) continue;

    // 日期匹配
    if (!tx.date) continue;
    const d = new Date(tx.date);
    if (isNaN(d.getTime())) continue;
    if (d.getFullYear() !== year) continue;

    if (period === "monthly") {
      if (d.getMonth() + 1 !== month) continue;
    } else if (period === "quarterly") {
      const txQuarter = Math.ceil((d.getMonth() + 1) / 3);
      if (txQuarter !== quarter) continue;
    }
    // yearly: 仅匹配年份即可

    const cat = matchTransactionCategory(tx.category);
    result[cat] += amount;
  }

  // 保留两位小数避免浮点误差
  for (const cat of ALL_CATEGORIES) {
    result[cat] = Math.round(result[cat] * 100) / 100;
  }
  return result;
}

/** 生成预算周期标签 */
function periodLabel(
  period: BudgetPeriod,
  year: number,
  month?: number,
  quarter?: number
): string {
  if (period === "monthly") return `${year}年${month}月`;
  if (period === "quarterly") return `${year}年 Q${quarter}`;
  return `${year}年`;
}

/** 计算执行率（百分比，保留 1 位小数） */
function calcExecutionRate(actual: number, budget: number): number {
  if (budget <= 0) return 0;
  return Math.round((actual / budget) * 1000) / 10;
}

/** 执行率对应颜色 */
function executionColor(rate: number): string {
  if (rate >= 100) return "#EF4444"; // 超支-红
  if (rate >= 80) return "#F59E0B"; // 临近-橙
  if (rate >= 50) return "#3B82F6"; // 正常-蓝
  return "#10B981"; // 充裕-绿
}

/** 查找匹配周期的预算 */
function findBudget(
  budgets: Budget[],
  period: BudgetPeriod,
  year: number,
  month?: number,
  quarter?: number
): Budget | undefined {
  return budgets.find(
    (b) =>
      b.period === period &&
      b.year === year &&
      b.month === month &&
      b.quarter === quarter
  );
}

// ============================================
// 主组件
// ============================================

export default function BudgetManager() {
  // 权限控制：财务中心仅超级管理员开放
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin());
  // 当前登录用户名（用于调整记录的 operator）
  const currentUser = useUserStore((s) => s.currentUser);

  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [transactions, setTransactions] = useState<FinanceTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  // 周期选择
  const now = new Date();
  const [period, setPeriod] = useState<BudgetPeriod>("monthly");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [quarter, setQuarter] = useState(getCurrentQuarter());

  // 弹窗状态
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);

  // 面板切换
  const [activePanel, setActivePanel] = useState<
    "analysis" | "history" | "versions"
  >("analysis");

  // ============================================
  // 加载数据
  // ============================================
  const loadData = useCallback(() => {
    setLoading(true);
    setBudgets(loadBudgets());
    setTransactions(loadTransactions());
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Phase 5：挂载时从 Vault 回填预算（Vault 为唯一真相源，localStorage 仅缓存）
  useEffect(() => {
    async function hydrateBudgetsFromVault() {
      try {
        const hydrated = await hydrateFinanceKey<Budget[]>("budgets.json", BUDGETS_KEY);
        if (hydrated) {
          setBudgets(hydrated);
        }
      } catch {
        /* Vault 不可达时保持本地数据 */
      }
    }
    void hydrateBudgetsFromVault();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // 当前选中周期的预算（动态聚合实际支出）
  // ============================================
  const currentBudget = useMemo(() => {
    const stored = findBudget(budgets, period, year, month, quarter);
    if (!stored) return undefined;

    // 动态聚合实际支出（从交易流水，确保数据始终最新）
    const actualMap = aggregateActualByCategory(
      transactions,
      period,
      year,
      month,
      quarter
    );

    const items: BudgetItem[] = stored.items.map((item) => ({
      ...item,
      actualAmount: actualMap[item.category] || 0,
    }));

    const totalBudget = items.reduce((s, i) => s + i.budgetedAmount, 0);
    const totalActual = items.reduce((s, i) => s + i.actualAmount, 0);

    return {
      ...stored,
      items,
      totalBudget,
      totalActual,
    } as Budget;
  }, [budgets, transactions, period, year, month, quarter]);

  // ============================================
  // 图表数据（预算 vs 实际）
  // ============================================
  const chartData = useMemo(() => {
    if (!currentBudget) return [];
    return currentBudget.items.map((item) => ({
      name: CATEGORY_META[item.category].label,
      category: item.category,
      预算: Math.round(item.budgetedAmount),
      实际: Math.round(item.actualAmount),
      isOver: item.actualAmount > item.budgetedAmount,
    }));
  }, [currentBudget]);

  // ============================================
  // 全局统计
  // ============================================
  const globalStats = useMemo(() => {
    if (!currentBudget) {
      return {
        totalBudget: 0,
        totalActual: 0,
        executionRate: 0,
        deviation: 0,
        overspendCount: 0,
        overspendAmount: 0,
      };
    }
    const totalBudget = currentBudget.totalBudget;
    const totalActual = currentBudget.totalActual;
    const executionRate = calcExecutionRate(totalActual, totalBudget);
    const deviation = totalActual - totalBudget;
    const overspendItems = currentBudget.items.filter(
      (i) => i.budgetedAmount > 0 && i.actualAmount > i.budgetedAmount
    );
    return {
      totalBudget,
      totalActual,
      executionRate,
      deviation,
      overspendCount: overspendItems.length,
      overspendAmount: overspendItems.reduce(
        (s, i) => s + (i.actualAmount - i.budgetedAmount),
        0
      ),
    };
  }, [currentBudget]);

  // ============================================
  // 保存预算（创建或调整）
  // ============================================
  const handleSaveBudget = useCallback(
    (data: {
      period: BudgetPeriod;
      year: number;
      month?: number;
      quarter?: number;
      items: Array<{ category: BudgetCategory; budgetedAmount: number }>;
    }) => {
      const nowIso = new Date().toISOString();
      const operator = currentUser?.displayName || currentUser?.username || "系统";

      // 检查是否已存在同周期预算
      const existing = findBudget(budgets, data.period, data.year, data.month, data.quarter);

      if (existing) {
        // 调整流程：记录调整历史 + 版本快照
        const adjustments: AdjustmentRecord[] = [];
        existing.items.forEach((oldItem) => {
          const newItem = data.items.find((i) => i.category === oldItem.category);
          if (newItem && newItem.budgetedAmount !== oldItem.budgetedAmount) {
            adjustments.push({
              timestamp: nowIso,
              category: oldItem.category,
              oldAmount: oldItem.budgetedAmount,
              newAmount: newItem.budgetedAmount,
              operator,
            });
          }
        });

        // 版本快照（保存调整前的状态）
        const prevVersion: BudgetVersion = {
          version: (existing.versions?.length || 0) + 1,
          snapshotAt: nowIso,
          items: existing.items.map((i) => ({ ...i })),
          totalBudget: existing.totalBudget,
          operator,
        };

        const updatedBudget: Budget = {
          ...existing,
          items: data.items.map((i) => ({
            category: i.category,
            budgetedAmount: i.budgetedAmount,
            actualAmount: 0, // 加载时动态聚合
          })),
          totalBudget: data.items.reduce((s, i) => s + i.budgetedAmount, 0),
          totalActual: 0,
          updatedAt: nowIso,
          adjustmentHistory: [
            ...(existing.adjustmentHistory || []),
            ...adjustments,
          ],
          versions: [...(existing.versions || []), prevVersion],
        };

        const next = budgets.map((b) =>
          b.id === existing.id ? updatedBudget : b
        );
        setBudgets(next);
        saveBudgets(next);
      } else {
        // 新建预算
        const newBudget: Budget = {
          id: genId(),
          period: data.period,
          year: data.year,
          month: data.month,
          quarter: data.quarter,
          items: data.items.map((i) => ({
            category: i.category,
            budgetedAmount: i.budgetedAmount,
            actualAmount: 0,
          })),
          totalBudget: data.items.reduce((s, i) => s + i.budgetedAmount, 0),
          totalActual: 0,
          createdAt: nowIso,
          updatedAt: nowIso,
          adjustmentHistory: [],
          versions: [],
        };
        const next = [newBudget, ...budgets];
        setBudgets(next);
        saveBudgets(next);
      }

      setShowEditModal(false);
      setEditingBudget(null);
    },
    [budgets, currentUser]
  );

  /** 删除预算 */
  const handleDeleteBudget = useCallback(() => {
    if (!currentBudget) return;
    const next = budgets.filter((b) => b.id !== currentBudget.id);
    setBudgets(next);
    saveBudgets(next);
  }, [budgets, currentBudget]);

  // ============================================
  // 周期切换辅助
  // ============================================
  const shiftPeriod = useCallback(
    (delta: number) => {
      if (period === "monthly") {
        let m = month + delta;
        let y = year;
        if (m < 1) {
          m = 12;
          y -= 1;
        } else if (m > 12) {
          m = 1;
          y += 1;
        }
        setMonth(m);
        setYear(y);
      } else if (period === "quarterly") {
        let q = quarter + delta;
        let y = year;
        if (q < 1) {
          q = 4;
          y -= 1;
        } else if (q > 4) {
          q = 1;
          y += 1;
        }
        setQuarter(q);
        setYear(y);
      } else {
        setYear(year + delta);
      }
    },
    [period, month, quarter, year]
  );

  /** 切换周期时重置为当前时间 */
  const handlePeriodChange = useCallback((p: BudgetPeriod) => {
    setPeriod(p);
    const n = new Date();
    setYear(n.getFullYear());
    setMonth(n.getMonth() + 1);
    setQuarter(getCurrentQuarter());
  }, []);

  // ============================================
  // 渲染：权限提示（非超管）
  // ============================================
  if (!isSuperAdmin) {
    return (
      <div className="h-full w-full overflow-hidden p-4">
        <div className="h-full flex flex-col items-center justify-center rounded-xl border border-red-500/20 bg-red-500/[0.02] backdrop-blur-xl">
          <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4">
            <Lock size={28} className="text-red-400" />
          </div>
          <h2 className="text-[16px] font-semibold text-white mb-2">权限受限</h2>
          <p className="text-[12px] font-mono text-gray-400 text-center max-w-sm leading-relaxed">
            预算管理仅对超级管理员开放。
            <br />
            普通员工无权查看预算数据。
          </p>
          <p className="text-[10px] font-mono text-gray-600 mt-4">
            如需访问，请联系超级管理员授权。
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // 主渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative h-full flex flex-col rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-cyan-500/15 border border-cyan-500/30">
            <Wallet size={13} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              预算管理
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Budget Manager · 预算制定 · 执行对比 · 超支预警
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 刷新 */}
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
            title="刷新数据"
          >
            {loading ? (
              <Loader2 size={11} className="text-gray-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* 周期选择栏 */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/[0.04] bg-white/[0.01]">
        {/* 周期切换 */}
        <div className="flex items-center rounded-md border border-white/[0.06] overflow-hidden">
          {(Object.keys(PERIOD_META) as BudgetPeriod[]).map((p) => (
            <button
              key={p}
              onClick={() => handlePeriodChange(p)}
              className={`px-2.5 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                period === p
                  ? "bg-cyan-500/20 text-cyan-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              {PERIOD_META[p].shortLabel}
            </button>
          ))}
        </div>

        {/* 时间导航 */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => shiftPeriod(-1)}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <ChevronLeft size={12} />
          </button>
          <span className="text-[11px] font-mono text-gray-200 min-w-[80px] text-center">
            {periodLabel(period, year, month, quarter)}
          </span>
          <button
            onClick={() => shiftPeriod(1)}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <ChevronRight size={12} />
          </button>
        </div>

        <div className="flex-1" />

        {/* 新建/编辑按钮 */}
        <button
          onClick={() => {
            setEditingBudget(currentBudget || null);
            setShowEditModal(true);
          }}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
        >
          {currentBudget ? (
            <Pencil size={11} className="text-cyan-400" />
          ) : (
            <Plus size={11} className="text-cyan-400" />
          )}
          <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">
            {currentBudget ? "调整预算" : "创建预算"}
          </span>
        </button>

        {/* 面板切换 */}
        {currentBudget && (
          <div className="flex items-center rounded-md border border-white/[0.06] overflow-hidden">
            <button
              onClick={() => setActivePanel("analysis")}
              className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                activePanel === "analysis"
                  ? "bg-cyan-500/20 text-cyan-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              分析
            </button>
            <button
              onClick={() => setActivePanel("history")}
              className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                activePanel === "history"
                  ? "bg-cyan-500/20 text-cyan-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              调整记录
            </button>
            <button
              onClick={() => setActivePanel("versions")}
              className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                activePanel === "versions"
                  ? "bg-cyan-500/20 text-cyan-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              版本
            </button>
          </div>
        )}
      </div>

      {/* 内容区 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 size={20} className="animate-spin text-cyan-400" />
            <span className="ml-2 text-[11px] font-mono text-gray-500">
              加载预算数据...
            </span>
          </div>
        ) : !currentBudget ? (
          /* 空状态 */
          <EmptyBudgetState
            onCreate={() => {
              setEditingBudget(null);
              setShowEditModal(true);
            }}
            periodLabel={periodLabel(period, year, month, quarter)}
          />
        ) : activePanel === "analysis" ? (
          /* 分析面板 */
          <>
            {/* 超支预警条 */}
            {globalStats.overspendCount > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-red-500/30 bg-red-500/[0.06]">
                <AlertTriangle size={12} className="text-red-400" />
                <span className="text-[10px] font-mono text-red-300 uppercase tracking-wider">
                  超支预警：{globalStats.overspendCount} 个类别超出预算 · 超支金额 {formatMoney(globalStats.overspendAmount)}
                </span>
              </div>
            )}

            {/* KPI 行 */}
            <div className="grid grid-cols-4 gap-2">
              <KpiCell
                label="预算总额"
                value={formatMoney(globalStats.totalBudget)}
                color="#06B6D4"
                icon={Target}
              />
              <KpiCell
                label="实际支出"
                value={formatMoney(globalStats.totalActual)}
                color={globalStats.totalActual > globalStats.totalBudget ? "#EF4444" : "#34D399"}
                icon={Coins}
              />
              <KpiCell
                label="执行率"
                value={`${globalStats.executionRate}%`}
                color={executionColor(globalStats.executionRate)}
                icon={Percent}
              />
              <KpiCell
                label="偏差"
                value={formatSignedMoney(globalStats.deviation)}
                color={globalStats.deviation > 0 ? "#EF4444" : "#10B981"}
                icon={globalStats.deviation > 0 ? TrendingUp : TrendingDown}
              />
            </div>

            {/* 预算 vs 实际 柱状图 */}
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Database size={11} className="text-cyan-400" />
                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                  预算 vs 实际 对比（按类别）
                </span>
              </div>
              {chartData.length === 0 ? (
                <div className="text-center py-6 text-[10px] font-mono text-gray-600">
                  暂无图表数据
                </div>
              ) : (
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} barGap={2}>
                      <CartesianGrid stroke="#ffffff10" strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                        axisLine={{ stroke: "#ffffff20" }}
                      />
                      <YAxis
                        tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                        axisLine={{ stroke: "#ffffff20" }}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#0F1623",
                          border: "1px solid #ffffff20",
                          borderRadius: 6,
                          fontSize: 11,
                          fontFamily: "monospace",
                        }}
                        labelStyle={{ color: "#E5E7EB" }}
                        formatter={(value: any) => formatMoney(Number(value))}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 10, fontFamily: "monospace" }}
                      />
                      <Bar dataKey="预算" fill="#06B6D4" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="实际" radius={[3, 3, 0, 0]}>
                        {chartData.map((entry, idx) => (
                          <Cell
                            key={idx}
                            fill={entry.isOver ? "#EF4444" : "#34D399"}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* 类别明细列表 */}
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Layers size={11} className="text-cyan-400" />
                  <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                    类别执行明细
                  </span>
                </div>
                <button
                  onClick={handleDeleteBudget}
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  title="删除此周期预算"
                >
                  <Trash2 size={9} />
                  删除
                </button>
              </div>
              <div className="space-y-1.5">
                {currentBudget.items.map((item) => (
                  <BudgetRow
                    key={item.category}
                    item={item}
                    onEdit={() => {
                      setEditingBudget(currentBudget);
                      setShowEditModal(true);
                    }}
                  />
                ))}
              </div>
            </div>

            {/* 数据来源说明 */}
            <div className="px-3 py-2 rounded-md border border-blue-500/15 bg-blue-500/[0.02] text-[10px] font-mono text-blue-300/70">
              <Database size={11} className="inline mr-1" />
              数据来源：预算 = localStorage(xeaos_finance_budgets) 人工录入 · 实际支出 = localStorage(xeaos_finance_transactions) 交易流水聚合
              <span className="ml-2 text-gray-500">
                · 共 {transactions.length} 笔交易记录
              </span>
            </div>
          </>
        ) : activePanel === "history" ? (
          /* 调整历史面板 */
          <HistoryPanel budget={currentBudget} />
        ) : (
          /* 版本管理面板 */
          <VersionPanel budget={currentBudget} />
        )}
      </div>

      {/* 编辑/创建弹窗 */}
      <AnimatePresence>
        {showEditModal && (
          <BudgetEditModal
            budget={editingBudget}
            period={period}
            year={year}
            month={month}
            quarter={quarter}
            onSubmit={handleSaveBudget}
            onClose={() => {
              setShowEditModal(false);
              setEditingBudget(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// KPI 单元格
// ============================================
function KpiCell({
  label,
  value,
  color,
  icon: Icon,
}: {
  label: string;
  value: string;
  color: string;
  icon: typeof Target;
}) {
  return (
    <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] px-3 py-2">
      <div className="flex items-center gap-1 mb-0.5">
        <Icon size={9} style={{ color }} />
        <span className="text-[8px] font-mono text-gray-500 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div
        className="text-[15px] font-mono font-semibold"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

// ============================================
// 空状态
// ============================================
function EmptyBudgetState({
  onCreate,
  periodLabel: pLabel,
}: {
  onCreate: () => void;
  periodLabel: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-14 h-14 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mb-3">
        <Wallet size={24} className="text-gray-600" />
      </div>
      <p className="text-[12px] text-gray-400 mb-1">暂无预算</p>
      <p className="text-[10px] font-mono text-gray-600 mb-4">
        {pLabel} · 点击创建该周期的预算
      </p>
      <button
        onClick={onCreate}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
      >
        <Plus size={12} className="text-cyan-400" />
        <span className="text-[10px] font-mono text-cyan-300 uppercase tracking-wider">
          创建预算
        </span>
      </button>
    </div>
  );
}

// ============================================
// 预算项行（执行率进度条 + 超支预警）
// ============================================
function BudgetRow({
  item,
  onEdit,
}: {
  item: BudgetItem;
  onEdit: () => void;
}) {
  const meta = CATEGORY_META[item.category];
  const rate = calcExecutionRate(item.actualAmount, item.budgetedAmount);
  const color = executionColor(rate);
  const isOver = item.budgetedAmount > 0 && item.actualAmount > item.budgetedAmount;
  const deviation = item.actualAmount - item.budgetedAmount;
  const overPercent =
    isOver && item.budgetedAmount > 0
      ? Math.round(((item.actualAmount - item.budgetedAmount) / item.budgetedAmount) * 1000) / 10
      : 0;

  return (
    <div
      className={`rounded-md border bg-white/[0.02] p-2.5 ${
        isOver ? "border-red-500/30 ring-1 ring-red-500/15" : "border-white/[0.04]"
      }`}
    >
      <div className="flex items-center gap-3">
        {/* 类别图标 */}
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
          style={{ background: `${meta.color}20`, border: `1px solid ${meta.color}40` }}
        >
          <Wallet size={12} style={{ color: meta.color }} />
        </div>

        {/* 类别 + 进度条 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[11px] text-gray-200 font-medium">
              {meta.label}
            </span>
            {isOver && (
              <span className="flex items-center gap-0.5 px-1 rounded text-[8px] font-mono uppercase bg-red-500/15 text-red-400">
                <AlertTriangle size={7} />
                超支 {overPercent}%
              </span>
            )}
          </div>
          {/* 执行率进度条 */}
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(100, rate)}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="h-full rounded-full"
                style={{ background: color }}
              />
            </div>
            <span
              className="text-[9px] font-mono flex-shrink-0"
              style={{ color }}
            >
              {rate}%
            </span>
          </div>
        </div>

        {/* 预算金额 */}
        <div className="flex-shrink-0 text-right w-[90px]">
          <div className="text-[8px] font-mono text-gray-600 uppercase">预算</div>
          <div className="text-[11px] font-mono text-gray-300">
            {formatMoney(item.budgetedAmount)}
          </div>
        </div>

        {/* 实际支出 */}
        <div className="flex-shrink-0 text-right w-[90px]">
          <div className="text-[8px] font-mono text-gray-600 uppercase">实际</div>
          <div
            className="text-[11px] font-mono"
            style={{ color: isOver ? "#EF4444" : "#34D399" }}
          >
            {formatMoney(item.actualAmount)}
          </div>
        </div>

        {/* 偏差 */}
        <div className="flex-shrink-0 text-right w-[90px]">
          <div className="text-[8px] font-mono text-gray-600 uppercase">偏差</div>
          <div
            className="text-[11px] font-mono"
            style={{ color: deviation > 0 ? "#EF4444" : deviation < 0 ? "#10B981" : "#9CA3AF" }}
          >
            {formatSignedMoney(deviation)}
          </div>
        </div>

        {/* 编辑 */}
        <button
          onClick={onEdit}
          className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-gray-300 transition-colors flex-shrink-0"
          title="调整预算"
        >
          <Pencil size={9} />
        </button>
      </div>
    </div>
  );
}

// ============================================
// 调整历史面板
// ============================================
function HistoryPanel({ budget }: { budget: Budget }) {
  const history = budget.adjustmentHistory || [];

  if (history.length === 0) {
    return (
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-6 text-center">
        <History size={24} className="text-gray-700 mx-auto mb-2" />
        <p className="text-[11px] text-gray-500 mb-1">暂无调整记录</p>
        <p className="text-[10px] font-mono text-gray-600">
          调整预算金额后将自动记录历史
        </p>
      </div>
    );
  }

  // 倒序展示（最新在前）
  const sorted = [...history].sort((a, b) =>
    b.timestamp.localeCompare(a.timestamp)
  );

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="flex items-center gap-1.5 mb-3">
        <History size={11} className="text-cyan-400" />
        <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
          预算调整历史（共 {history.length} 条）
        </span>
      </div>
      <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
        {sorted.map((rec, idx) => {
          const meta = CATEGORY_META[rec.category];
          const diff = rec.newAmount - rec.oldAmount;
          const isIncrease = diff > 0;
          return (
            <div
              key={idx}
              className="flex items-center gap-3 px-2.5 py-2 rounded border border-white/[0.04] bg-white/[0.01]"
            >
              {/* 类别色标 */}
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: meta.color }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-gray-200">{meta.label}</span>
                  <span className="text-[9px] font-mono text-gray-600">
                    {formatDateTime(rec.timestamp)}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5 text-[10px] font-mono">
                  <span className="text-gray-500">{formatMoney(rec.oldAmount)}</span>
                  <span style={{ color: isIncrease ? "#10B981" : "#EF4444" }}>
                    {isIncrease ? "→" : "←"}
                  </span>
                  <span className="text-gray-300">{formatMoney(rec.newAmount)}</span>
                  <span
                    className="ml-1"
                    style={{ color: isIncrease ? "#10B981" : "#EF4444" }}
                  >
                    {isIncrease ? "+" : ""}
                    {formatSignedMoney(diff)}
                  </span>
                </div>
              </div>
              <span className="text-[9px] font-mono text-gray-600 flex-shrink-0">
                {rec.operator}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// 版本管理面板
// ============================================
function VersionPanel({ budget }: { budget: Budget }) {
  const versions = budget.versions || [];

  if (versions.length === 0) {
    return (
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-6 text-center">
        <Layers size={24} className="text-gray-700 mx-auto mb-2" />
        <p className="text-[11px] text-gray-500 mb-1">暂无历史版本</p>
        <p className="text-[10px] font-mono text-gray-600">
          调整预算后将自动保存版本快照
        </p>
      </div>
    );
  }

  // 倒序展示（最新版本在前）
  const sorted = [...versions].sort((a, b) => b.version - a.version);

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="flex items-center gap-1.5 mb-3">
        <Layers size={11} className="text-cyan-400" />
        <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
          预算版本管理（共 {versions.length} 个历史版本）
        </span>
      </div>
      <div className="space-y-2 max-h-[420px] overflow-y-auto">
        {/* 当前版本 */}
        <div className="flex items-center gap-2 px-2.5 py-2 rounded border border-cyan-500/30 bg-cyan-500/[0.05]">
          <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-cyan-500/20 text-cyan-300">
            V{versions.length + 1}
          </span>
          <div className="flex-1">
            <div className="text-[11px] text-gray-200">当前版本</div>
            <div className="text-[9px] font-mono text-gray-500">
              总预算 {formatMoney(budget.totalBudget)} · {budget.items.length} 个类别
            </div>
          </div>
          <span className="text-[9px] font-mono text-cyan-400">活跃中</span>
        </div>

        {/* 历史版本 */}
        {sorted.map((ver) => (
          <div
            key={ver.version}
            className="px-2.5 py-2 rounded border border-white/[0.04] bg-white/[0.01]"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-white/[0.06] text-gray-400">
                V{ver.version}
              </span>
              <span className="text-[10px] font-mono text-gray-500">
                {formatDateTime(ver.snapshotAt)}
              </span>
              <div className="flex-1" />
              <span className="text-[10px] font-mono text-gray-300">
                总预算 {formatMoney(ver.totalBudget)}
              </span>
              <span className="text-[9px] font-mono text-gray-600">
                {ver.operator}
              </span>
            </div>
            {/* 版本类别明细 */}
            <div className="flex flex-wrap gap-1">
              {ver.items.map((item) => {
                const meta = CATEGORY_META[item.category];
                return (
                  <span
                    key={item.category}
                    className="px-1.5 py-0.5 rounded text-[9px] font-mono border"
                    style={{
                      background: `${meta.color}15`,
                      borderColor: `${meta.color}30`,
                      color: meta.color,
                    }}
                    title={`${meta.label}：${formatMoney(item.budgetedAmount)}`}
                  >
                    {meta.label} {formatMoney(item.budgetedAmount)}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// 预算编辑/创建弹窗
// ============================================
function BudgetEditModal({
  budget,
  period,
  year,
  month,
  quarter,
  onSubmit,
  onClose,
}: {
  budget: Budget | null;
  period: BudgetPeriod;
  year: number;
  month: number;
  quarter: number;
  onSubmit: (data: {
    period: BudgetPeriod;
    year: number;
    month?: number;
    quarter?: number;
    items: Array<{ category: BudgetCategory; budgetedAmount: number }>;
  }) => void;
  onClose: () => void;
}) {
  // 初始化各类别金额（编辑时读取已有，新建时为 0）
  const [amounts, setAmounts] = useState<Record<BudgetCategory, number>>(() => {
    const init = {} as Record<BudgetCategory, number>;
    for (const cat of ALL_CATEGORIES) {
      init[cat] = 0;
    }
    if (budget) {
      budget.items.forEach((item) => {
        init[item.category] = item.budgetedAmount;
      });
    }
    return init;
  });
  const [error, setError] = useState<string | null>(null);

  const totalBudget = useMemo(
    () => ALL_CATEGORIES.reduce((s, cat) => s + (amounts[cat] || 0), 0),
    [amounts]
  );

  const handleSubmit = () => {
    // 校验：至少一个类别金额 > 0
    const hasAny = ALL_CATEGORIES.some((cat) => (amounts[cat] || 0) > 0);
    if (!hasAny) {
      setError("请至少为一个类别录入预算金额");
      return;
    }
    // 校验：金额不能为负
    if (ALL_CATEGORIES.some((cat) => (amounts[cat] || 0) < 0)) {
      setError("预算金额不能为负");
      return;
    }

    onSubmit({
      period,
      year,
      month: period === "monthly" ? month : undefined,
      quarter: period === "quarterly" ? quarter : undefined,
      items: ALL_CATEGORIES.map((cat) => ({
        category: cat,
        budgetedAmount: Math.round((amounts[cat] || 0) * 100) / 100,
      })),
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-xl border border-cyan-500/30 bg-[#0F1623] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
              {budget ? (
                <Pencil size={13} className="text-cyan-400" />
              ) : (
                <Plus size={13} className="text-cyan-400" />
              )}
            </div>
            <div>
              <h3 className="text-[13px] font-semibold text-white">
                {budget ? "调整预算" : "创建预算"}
              </h3>
              <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                {PERIOD_META[period].label} · {periodLabel(period, year, month, quarter)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 表单 */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {error && (
            <div className="px-3 py-2 rounded border border-red-500/30 bg-red-500/10 text-[10px] text-red-300 font-mono">
              {error}
            </div>
          )}

          {/* 周期信息（只读，调整时锁定） */}
          <div className="px-3 py-2 rounded-md border border-white/[0.06] bg-white/[0.02]">
            <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1">
              预算周期
            </div>
            <div className="text-[12px] text-gray-200">
              {periodLabel(period, year, month, quarter)}
              {budget && (
                <span className="ml-2 text-[10px] font-mono text-amber-400">
                  · 调整将记录历史并保存版本快照
                </span>
              )}
            </div>
          </div>

          {/* 各类别预算金额录入 */}
          <div>
            <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-2">
              类别预算金额（元）
            </label>
            <div className="space-y-2">
              {ALL_CATEGORIES.map((cat) => {
                const meta = CATEGORY_META[cat];
                return (
                  <div
                    key={cat}
                    className="flex items-center gap-3 px-2.5 py-2 rounded-md border border-white/[0.04] bg-white/[0.01]"
                  >
                    <div
                      className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
                      style={{ background: `${meta.color}20`, border: `1px solid ${meta.color}40` }}
                    >
                      <Wallet size={10} style={{ color: meta.color }} />
                    </div>
                    <span className="text-[11px] text-gray-200 w-[60px] flex-shrink-0">
                      {meta.label}
                    </span>
                    <div className="flex-1" />
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] font-mono text-gray-600">¥</span>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={amounts[cat] || ""}
                        onChange={(e) =>
                          setAmounts({
                            ...amounts,
                            [cat]: Math.max(0, Number(e.target.value) || 0),
                          })
                        }
                        placeholder="0"
                        className="w-[120px] px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 text-right font-mono placeholder-gray-600 outline-none focus:border-cyan-500/30"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 总预算汇总 */}
          <div className="flex items-center justify-between px-3 py-2 rounded-md border border-cyan-500/20 bg-cyan-500/[0.04]">
            <span className="text-[10px] font-mono text-cyan-300 uppercase tracking-wider">
              预算总额
            </span>
            <span className="text-[14px] font-mono font-semibold text-cyan-300">
              {formatMoney(totalBudget)}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-[10px] font-mono text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cyan-500/20 border border-cyan-500/40 hover:bg-cyan-500/30 transition-colors"
          >
            <Save size={11} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-cyan-300 uppercase tracking-wider">
              {budget ? "保存调整" : "创建预算"}
            </span>
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
